import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { OnlinePaymentDetails } from '../types';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AIChatButton from '../components/AIChatButton';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';

const OrderValidation: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [onlinePayments, setOnlinePayments] = useState<OnlinePaymentDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnlinePayments = useCallback(async () => {
    if (!currentUser) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query OnlinePayment collection for current user
      const onlinePaymentRef = collection(db, 'OnlinePayment');
      const q = query(
        onlinePaymentRef,
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const payments: OnlinePaymentDetails[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        payments.push({
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate(),
        } as OnlinePaymentDetails);
      });

      setOnlinePayments(payments);
    } catch (error) {
      console.error('Error fetching online payments:', error);
      setError('Failed to load payment records');
      toast.error('Failed to load payment records');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    fetchOnlinePayments();
  }, [currentUser, navigate, fetchOnlinePayments]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'failed':
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      case 'processing':
        return <ClockIcon className="h-6 w-6 text-blue-600" />;
      case 'pending':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />;
      default:
        return <ClockIcon className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Payment Completed';
      case 'failed':
        return 'Payment Failed';
      case 'processing':
        return 'Processing Payment';
      case 'pending':
        return 'Pending Approval';
      default:
        return 'Unknown Status';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Payments</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchOnlinePayments}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Order Validation</h1>
              <p className="mt-2 text-gray-600">
                Track your online payment status and order approval
              </p>
            </div>
            <button
              onClick={fetchOnlinePayments}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <DocumentTextIcon className="h-5 w-5" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Payment Records */}
        {onlinePayments.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Records</h3>
            <p className="text-gray-600">
              You haven't made any online payments yet. Your payment records will appear here once you complete a purchase.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {onlinePayments.map((payment) => (
              <div
                key={payment.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                {/* Payment Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(payment.status)}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Payment #{payment.id.split('_')[1]}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {formatDate(payment.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                          payment.status
                        )}`}
                      >
                        {getStatusText(payment.status)}
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        {formatCurrency(payment.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Details */}
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Customer Information */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Customer Information</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p><span className="font-medium">Name:</span> {payment.customerInfo.firstName} {payment.customerInfo.lastName}</p>
                        <p><span className="font-medium">Email:</span> {payment.customerInfo.email}</p>
                        <p><span className="font-medium">Phone:</span> {payment.customerInfo.phone}</p>
                        <p><span className="font-medium">Address:</span> {payment.customerInfo.address}, {payment.customerInfo.city}</p>
                      </div>
                    </div>

                    {/* Payment Information */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Payment Information</h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <p><span className="font-medium">Method:</span> {payment.paymentDetails.method}</p>
                        <p><span className="font-medium">Transaction Ref:</span> {payment.paymentDetails.transactionRef}</p>
                        <p><span className="font-medium">Currency:</span> {payment.currency}</p>
                        <p><span className="font-medium">Items:</span> {payment.items.length} item(s)</p>
                      </div>
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      {shouldShowIOSWebViewAlert() ? "Items Added" : "Items Purchased"}
                    </h4>
                    <div className="space-y-2">
                      {payment.items.map((item, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">
                              {index + 1}.
                            </span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.name}</p>
                              <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(item.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Admin Notes */}
                  {payment.adminNotes && (
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Admin Notes</h4>
                      <p className="text-sm text-blue-800">{payment.adminNotes}</p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-500">
                      <div>
                        <span className="font-medium">Created:</span> {formatDate(payment.createdAt)}
                      </div>
                      <div>
                        <span className="font-medium">Updated:</span> {formatDate(payment.updatedAt)}
                      </div>
                      {payment.completedAt && (
                        <div>
                          <span className="font-medium">Completed:</span> {formatDate(payment.completedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default OrderValidation;
